import React, { useCallback, useEffect, useRef, useState } from 'react';
import './styles.scss';

// Mirrors fishingSystem.ts's wire format exactly - see that file's doc comment
// for why the server, not this component, owns pass/fail.
// The wait for a bite is not in here: it happens before this component ever
// shows. The server holds the line in the water, and only sends the start
// packet once something has taken the bait.
interface FishingStep {
  durationMs: number;
  prepMs: number;
  zoneWidthPct: number;
  sampleIntervalMs: number;
}

interface ShowDetail {
  requestId: number;
  biomeLabel: string;
  step: FishingStep;
}

const RESULT_PREFIX = 'fishing-minigame-result:';

// Needle physics - not server-visible, purely how the fight feels. Rising
// faster than it falls makes holding the active choice and releasing the
// recovery, rather than the other way round.
const NEEDLE_RISE_PER_MS = 0.085;
const NEEDLE_FALL_PER_MS = 0.052;

// The zone drifts because a fish runs. This is the whole difference between
// this and the forge gauge, where a temperature sits still: there you hold a
// value, here you chase one.
const ZONE_DRIFT_PER_MS = 0.016;
const DRIFT_TURN_MIN_MS = 650;
const DRIFT_TURN_SPAN_MS = 500;

// Line strain. It builds only while pulling PAST the zone - holding correctly
// costs nothing, forcing does. Snapping ends the fight at once, and is the one
// way to lose that is entirely the player's own doing.
const STRAIN_RISE_PER_MS = 0.05;
const STRAIN_FALL_PER_MS = 0.07;
const STRAIN_HOT = 55;

// What the client believes it scored. The server re-derives its own verdict
// from the sample series; this only decides which word the overlay shows on
// its way out.
const LOCAL_PASS_RATIO = 0.7;

const FishingMinigame: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [biomeLabel, setBiomeLabel] = useState('');
  const [phase, setPhase] = useState<'prep' | 'fight'>('prep');
  const [needle, setNeedle] = useState(50);
  const [zoneLeft, setZoneLeft] = useState(37);
  const [zoneWidth, setZoneWidth] = useState(26);
  const [strain, setStrain] = useState(0);
  const [outcome, setOutcome] = useState<'none' | 'ok' | 'fail'>('none');

  const requestRef = useRef<number | null>(null);
  const stepRef = useRef<FishingStep | null>(null);
  const holdingRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const samplesRef = useRef<boolean[]>([]);
  // Mirrors `phase` for the keydown closure, which does not depend on it and
  // would otherwise read a stale value across the transition into the fight.
  const liveRef = useRef(false);

  const send = useCallback((requestId: number, samples: boolean[]) => {
    const w = window as unknown as { skyrimPlatform?: { sendMessage?: (m: string) => void } };
    w.skyrimPlatform?.sendMessage?.(RESULT_PREFIX + JSON.stringify({ requestId, samples }));
  }, []);

  useEffect(() => {
    const showHandler = (e: Event) => {
      const d = (e as CustomEvent).detail as ShowDetail;
      if (!d || !d.step) return;
      requestRef.current = d.requestId;
      stepRef.current = d.step;
      samplesRef.current = [];
      setBiomeLabel(d.biomeLabel || '');
      setZoneWidth(d.step.zoneWidthPct);
      setZoneLeft(50 - d.step.zoneWidthPct / 2);
      setNeedle(50);
      setStrain(0);
      setOutcome('none');
      setPhase('prep');
      liveRef.current = false;
      setVisible(true);
    };
    const hideHandler = () => setVisible(false);

    window.addEventListener('skymp5-client:showFishingMinigame', showHandler);
    window.addEventListener('skymp5-client:hideFishingMinigame', hideHandler);
    return () => {
      window.removeEventListener('skymp5-client:showFishingMinigame', showHandler);
      window.removeEventListener('skymp5-client:hideFishingMinigame', hideHandler);
    };
  }, []);

  // The fight itself: a prep beat with nothing scored, then one RAF loop that
  // moves the needle, drifts the zone, builds strain and samples.
  useEffect(() => {
    const step = stepRef.current;
    if (!visible || !step) return;

    let cancelled = false;
    let pos = 50;
    let zoneC = 50;
    let dir = Math.random() < 0.5 ? -1 : 1;
    let str = 0;
    let last = 0;
    let t0 = 0;
    let nextTurn = 0;
    let lastSample = 0;
    const half = step.zoneWidthPct / 2;

    const settle = (done: 'ok' | 'fail') => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setOutcome(done);
      const rid = requestRef.current;
      const samples = samplesRef.current;
      // Held briefly so the result lands on the bar the player was watching,
      // instead of the overlay vanishing mid-motion.
      setTimeout(() => {
        if (rid !== null) send(rid, samples);
        setVisible(false);
      }, 700);
    };

    const tick = (now: number) => {
      if (cancelled) return;
      const dt = Math.min(48, now - last);
      last = now;

      if (now - t0 >= step.durationMs) {
        const inZone = samplesRef.current.filter(Boolean).length;
        const ratio = samplesRef.current.length ? inZone / samplesRef.current.length : 0;
        settle(ratio >= LOCAL_PASS_RATIO ? 'ok' : 'fail');
        return;
      }

      pos += holdingRef.current ? dt * NEEDLE_RISE_PER_MS : -dt * NEEDLE_FALL_PER_MS;
      if (pos < 0) pos = 0;
      if (pos > 100) pos = 100;
      setNeedle(pos);

      if (now >= nextTurn) {
        dir = Math.random() < 0.5 ? -1 : 1;
        nextTurn = now + DRIFT_TURN_MIN_MS + Math.random() * DRIFT_TURN_SPAN_MS;
      }
      zoneC += dir * dt * ZONE_DRIFT_PER_MS;
      if (zoneC < half) { zoneC = half; dir = 1; }
      if (zoneC > 100 - half) { zoneC = 100 - half; dir = -1; }
      setZoneLeft(zoneC - half);

      str += pos > zoneC + half ? dt * STRAIN_RISE_PER_MS : -dt * STRAIN_FALL_PER_MS;
      if (str < 0) str = 0;
      setStrain(str);
      if (str >= 100) {
        settle('fail');
        return;
      }

      if (now - lastSample >= step.sampleIntervalMs) {
        lastSample = now;
        samplesRef.current.push(pos >= zoneC - half && pos <= zoneC + half);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    const prepTimer = setTimeout(() => {
      if (cancelled) return;
      setPhase('fight');
      liveRef.current = true;
      t0 = last = lastSample = performance.now();
      nextTurn = t0 + DRIFT_TURN_MIN_MS;
      rafRef.current = requestAnimationFrame(tick);
    }, step.prepMs);

    return () => {
      cancelled = true;
      clearTimeout(prepTimer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      holdingRef.current = false;
    };
  }, [visible, send]);

  useEffect(() => {
    if (!visible) return;
    const down = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || !liveRef.current) return;
      e.preventDefault();
      holdingRef.current = true;
    };
    const up = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      e.preventDefault();
      holdingRef.current = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, [visible]);

  if (!visible) return null;

  let title = 'Il court, ramène-le';
  const sub = biomeLabel;
  if (phase === 'prep') {
    title = 'Ça mord !';
  } else if (outcome === 'ok') {
    title = 'Ramené';
  } else if (outcome === 'fail') {
    title = 'Décroché';
  }

  return (
    <div className="fishing-minigame">
      <div className={`fishing-minigame__shell fishing-minigame__shell--${outcome}`}>
        <div className="fishing-minigame__head">
          <div className="fishing-minigame__title">{title}</div>
          {sub && <div className="fishing-minigame__sub">{sub}</div>}
        </div>

        <div className={`fishing-minigame__track${phase === 'fight' ? '' : ' fishing-minigame__track--prepping'}`}>
          <div
            className="fishing-minigame__zone"
            style={{ left: `${zoneLeft}%`, width: `${zoneWidth}%` }}
          />
          <div className="fishing-minigame__needle" style={{ left: `${needle}%` }} />
        </div>

        <div className="fishing-minigame__strain-row">
          <span className="fishing-minigame__strain-label">Tension de la ligne</span>
          <div className="fishing-minigame__strain">
            <div
              className="fishing-minigame__strain-fill"
              data-hot={strain > STRAIN_HOT ? '1' : '0'}
              style={{ width: `${Math.min(100, strain)}%` }}
            />
          </div>
        </div>

        <div className="fishing-minigame__hint">
          <kbd>Espace</kbd> maintenir pour monter, relâcher pour descendre
        </div>
      </div>
    </div>
  );
};

export default FishingMinigame;
